import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SEO_DEFAULTS } from 'config';
import { SEOData } from 'utils/types';

const BASE_URL = 'https://abbeydentalsupplies.com';

function toAbsoluteAssetUrl(src: string): string {
  if (src.startsWith('https://') || src.startsWith('http://')) return src;
  if (src.startsWith('//')) return `https:${src}`;
  const path = src.startsWith('/') ? src : `/${src}`;
  return `${BASE_URL}${path}`;
}

interface SEOProps extends Partial<SEOData> {
  noIndex?: boolean;
  children?: React.ReactNode;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  noIndex = false,
  children,
}) => {
  const { pathname } = useLocation();

  const seoTitle = title ? `${title} | ${SEO_DEFAULTS.title}` : SEO_DEFAULTS.title;
  const seoDescription = description || SEO_DEFAULTS.description;
  const seoKeywords = keywords ? [...SEO_DEFAULTS.keywords, ...keywords].join(', ') : SEO_DEFAULTS.keywords.join(', ');
  const seoImage = toAbsoluteAssetUrl(image || SEO_DEFAULTS.image);
  const seoUrl = url || `${BASE_URL}${pathname}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />
      <meta name="author" content="Abbey Dental Supplies" />

      {/* Canonical URL */}
      <link rel="canonical" href={seoUrl} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Abbey Dental Supplies" />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@AbbeyDentalSupp" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />

      {children}
    </Helmet>
  );
};

export default SEO;
